import React from "react";
import { Link } from "react-router-dom";
import type { PaymentRecord } from "../../../../mocks/payments";

export type EnrichedPaymentRowItem = PaymentRecord & {
  fisNo?: string;
  kgRefText?: string;
  kgRefMode?: string;
  aciklamaTipi?: string;
};

type Props = {
  payment: EnrichedPaymentRowItem;
  nameHighlight?: string;
  phoneHighlight?: string;
  onEdit: () => void;
  onDelete: () => void;
};

function hl(text: string, q: string) {
  if (!q.trim()) return <span>{text}</span>;

  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <span>{text}</span>;

  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-amber-200 text-amber-900 rounded-sm px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </span>
  );
}

function formatDate(iso: string) {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const PaymentRow: React.FC<Props> = ({
  payment,
  nameHighlight = "",
  phoneHighlight = "",
  onEdit,
  onDelete,
}) => {
  const isPresalePayment = payment.animalId <= 0 || payment.shareId <= 0;

  return (
    <>
      <div
        className="hidden lg:grid items-center text-xs font-body border-b border-farm-border last:border-0 hover:bg-farm-gold-pale/20 transition-colors"
        style={{
          gridTemplateColumns: "82px 150px 120px 88px 88px 110px 88px 88px 68px 120px 66px",
          minWidth: "1180px",
        }}
      >
        <div className="px-2 py-2 text-farm-muted font-mono">
          {formatDate(payment.tarih)}
        </div>

        <div className="px-2 py-2 min-w-0">
          <Link
            to={`/panel/musteriler/${payment.musteriId}`}
            className="text-farm-brown font-semibold hover:text-farm-earth transition-colors cursor-pointer truncate block"
          >
            {hl(payment.musteriAdi, nameHighlight)}
          </Link>
        </div>

        <div className="px-2 py-2">
          <a
            href={`tel:${payment.telefon}`}
            className="text-farm-muted font-mono hover:text-farm-earth cursor-pointer transition-colors"
          >
            {hl(payment.telefon, phoneHighlight)}
          </a>
        </div>

        <div className="px-2 py-2 min-w-0">
          {isPresalePayment ? (
            <span className="block truncate font-mono text-[10px] font-semibold text-farm-earth">
              {payment.hayvanKupeNo}
            </span>
          ) : (
            <Link
              to={`/panel/hayvanlar/${payment.animalId}`}
              className="text-farm-brown hover:text-farm-earth cursor-pointer transition-colors block truncate font-mono text-[10px]"
            >
              {payment.hayvanKupeNo}
            </Link>
          )}
        </div>

        <div className="px-2 py-2 font-mono text-[10px] text-farm-muted truncate">
          {payment.hisseNo || <span className="text-farm-muted/30">—</span>}
        </div>

        <div className="px-2 py-2 text-[10px] text-farm-muted truncate" title={payment.kgRefText}>
          {payment.kgRefText || <span className="text-farm-muted/30">—</span>}
        </div>

        <div className="px-2 py-2 font-mono text-[10px] text-farm-muted truncate" title={payment.fisNo}>
          {payment.fisNo || <span className="text-farm-muted/30">—</span>}
        </div>

        <div className="px-2 py-2 font-bold text-farm-green whitespace-nowrap text-right">
          {fmt(payment.tutar)}
        </div>

        <div className="px-2 py-2">
          <span
            className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
              payment.tip === "Nakit"
                ? "bg-farm-green-pale text-farm-green"
                : "bg-sky-50 text-sky-600"
            }`}
          >
            {payment.tip}
          </span>
        </div>

        <div
          className="px-2 py-2 text-farm-muted truncate max-w-[120px]"
          title={payment.aciklama}
        >
          {payment.aciklama || <span className="text-farm-muted/40">—</span>}
        </div>

        <div className="px-2 py-2 flex items-center gap-1">
          <button
            onClick={onEdit}
            className="w-6 h-6 flex items-center justify-center rounded text-farm-earth hover:bg-farm-earth hover:text-white transition-colors cursor-pointer"
            title="Düzenle"
            type="button"
          >
            <i className="ri-edit-line text-sm" />
          </button>
          <button
            onClick={onDelete}
            className="w-6 h-6 flex items-center justify-center rounded text-red-400 hover:bg-red-500 hover:text-white transition-colors cursor-pointer"
            title="Sil"
            type="button"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>

      <div className="lg:hidden border-b border-farm-border last:border-0 px-3 py-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0">
            <Link
              to={`/panel/musteriler/${payment.musteriId}`}
              className="text-sm font-semibold text-farm-brown cursor-pointer hover:text-farm-earth transition-colors"
            >
              {hl(payment.musteriAdi, nameHighlight)}
            </Link>
            <p className="text-xs font-mono text-farm-muted mt-0.5">
              {hl(payment.telefon, phoneHighlight)}
            </p>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="font-bold text-farm-green text-sm">
              {fmt(payment.tutar)}
            </span>
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                payment.tip === "Nakit"
                  ? "bg-farm-green-pale text-farm-green"
                  : "bg-sky-50 text-sky-600"
              }`}
            >
              {payment.tip}
            </span>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1.5">
          <span className="text-[10px] text-farm-muted font-mono">{formatDate(payment.tarih)}</span>
          {isPresalePayment ? (
            <span className="text-[10px] font-mono font-semibold text-farm-earth">
              {payment.hayvanKupeNo}
            </span>
          ) : (
            <Link
              to={`/panel/hayvanlar/${payment.animalId}`}
              className="text-[10px] font-mono text-farm-earth hover:underline cursor-pointer"
            >
              {payment.hayvanKupeNo}
            </Link>
          )}
          <span className="text-[10px] text-farm-muted">{payment.hisseNo}</span>
          {payment.fisNo ? (
            <span className="text-[10px] font-mono text-farm-muted bg-farm-cream border border-farm-border px-1 rounded">
              Fiş: {payment.fisNo}
            </span>
          ) : null}
        </div>

        {payment.kgRefText ? (
          <p className="text-[10px] text-farm-muted mb-1">KG/REF: {payment.kgRefText}</p>
        ) : null}

        {payment.aciklama && (
          <p className="text-[10px] text-farm-muted italic mb-1.5">"{payment.aciklama}"</p>
        )}

        <div className="flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-1 text-[10px] text-farm-earth font-semibold cursor-pointer whitespace-nowrap hover:underline"
              type="button"
            >
              <i className="ri-edit-line" />
              Düzenle
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-1 text-[10px] text-red-400 font-semibold cursor-pointer whitespace-nowrap hover:underline"
              type="button"
            >
              <i className="ri-delete-bin-line" />
              Sil
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentRow;
